import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from 'react';
import { createPortal } from 'react-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import {
  FormInputArtifact,
  FormField,
  DesiredAction,
  DesiredActionKey,
  DesiredActionResponse,
  AnalyzeTodoClarifyingQuestion,
} from '@dreamai/types';
import getSymbolFromCurrency from 'currency-symbol-map';
import { useRadiusCheck } from '../../../hooks/useRadiusCheck';
import { ReactComponent as ChevronLeftIcon } from '../../../icons/chevron-left.svg';
import { ReactComponent as CloseXIcon } from '../../../icons/close-x.svg';
import { ReactComponent as ChevronDownIcon } from '../../../icons/chevron-down.svg';
import { ReactComponent as SpeechBubbleIcon } from '../../../icons/speech-bubble.svg';
import { ReactComponent as NoLongerNeededIcon } from '../../../icons/no-longer-needed.svg';
import { ReactComponent as ActionBuyIcon } from '../../../icons/action-buy.svg';
import { ReactComponent as ActionResearchIcon } from '../../../icons/action-research.svg';
import { ReactComponent as ActionEmailSendIcon } from '../../../icons/action-email-send.svg';
import { ReactComponent as ActionBuyAndActIcon } from '../../../icons/action-buy-and-act.svg';
import { ReactComponent as ActionCalendarInviteIcon } from '../../../icons/action-calendar-invite.svg';
import { ReactComponent as ActionBringAndBuyIcon } from '../../../icons/action-bring-and-buy.svg';
import { ReactComponent as ActionDraftEmailIcon } from '../../../icons/action-draft-email.svg';
import { ReactComponent as ActionPhoneIcon } from '../../../icons/action-phone.svg';
import { ReactComponent as ActionPresentOptionsIcon } from '../../../icons/action-present-options.svg';
import { ReactComponent as ActionFillInIcon } from '../../../icons/action-fill-in.svg';
import { ReactComponent as ActionReserveIcon } from '../../../icons/action-reserve.svg';
import { ReactComponent as ActionActIcon } from '../../../icons/action-act.svg';
import { ReactComponent as ActionOtherIcon } from '../../../icons/action-other.svg';
import { ReactComponent as TransportItineraryIcon } from '../../../icons/transport-itinerary.svg';
import { ReactComponent as ActionProjectIcon } from '../../../icons/action-project.svg';
import { ReactComponent as MagicWandIcon } from '../../../icons/magic-wand.svg';
import { ReactComponent as ActionCreateIcon } from '../../../icons/action-create.svg';
import {
  getActionLabel,
  getActionKeyLabel,
  SPENDING_ACTION_KEYS,
  getQuestionSpendApproval,
} from '../../../lib/desiredActionFields';
import SpendingApprovalModal, { CardSelection } from './SpendingApprovalModal';
import { ReplyToItem } from '../../../contexts/ReplyToContext';
import { useMobileNav } from '../../../contexts/MobileNavContext';
import './form-input-full-screen.scss';
import './multiple-choice-full-screen.scss';

const ACTION_ICONS: Record<
  DesiredActionKey,
  React.FC<React.SVGProps<SVGSVGElement>>
> = {
  buy: ActionBuyIcon,
  reserve: ActionReserveIcon,
  research: ActionResearchIcon,
  present_options: ActionPresentOptionsIcon,
  email_from_assistant: ActionEmailSendIcon,
  draft_email_from_user: ActionDraftEmailIcon,
  phone_from_assistant: ActionPhoneIcon,
  fill_in_by_assistant: ActionFillInIcon,
  send_calendar_invite: ActionCalendarInviteIcon,
  bring: TransportItineraryIcon,
  bring_and_buy: ActionBringAndBuyIcon,
  act: ActionActIcon,
  buy_and_act: ActionBuyAndActIcon,
  project: ActionProjectIcon,
  autopilot: MagicWandIcon,
  other: ActionOtherIcon,
  enhance: ActionBuyIcon,
  find_more: ActionResearchIcon,
  create: ActionCreateIcon,
};

interface FormInputFullScreenProps {
  artifact: FormInputArtifact;
  formValues: Record<string, string | number | null>;
  onFieldChange: (fieldId: string, value: string | number | null) => void;
  onSubmit: () => void;
  onSubmitWithAction: (
    desiredAction: DesiredActionResponse,
    mandateItems?: Array<{
      actionLine: string;
      descriptionLine: string;
      price: number;
      price_currency: string;
      isDeposit?: boolean;
    }>,
    cardSelection?: CardSelection,
  ) => Promise<void>;
  onAcknowledge: () => void;
  onClose: () => void;
  submitting: boolean;
  readOnly?: boolean;
  pendingSpendItems: Array<{
    id: string;
    actionLine: string;
    descriptionLine: string;
    price: number;
    price_currency: string;
    purchaseSummary?: string;
    purchaseLink?: string;
    delivery?: string;
    deliveryAddress?: string;
    isDeposit?: boolean;
  }>;
  onApproveSpending: (cardSel?: CardSelection) => void;
  onChatAbout?: () => void;
  chatButtonText: string;
  agentName?: string;
  agentImageUrl?: string;
}

const FormInputFullScreen: React.FC<FormInputFullScreenProps> = ({
  artifact,
  formValues,
  onFieldChange,
  onSubmit,
  onSubmitWithAction,
  onAcknowledge,
  onClose,
  submitting,
  readOnly = false,
  pendingSpendItems,
  onApproveSpending,
  onChatAbout,
  chatButtonText,
  agentName,
  agentImageUrl,
}) => {
  const { isMobile } = useMobileNav();
  const [showSpendApproval, setShowSpendApproval] = useState(false);
  const [actionDropdownOpen, setActionDropdownOpen] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [selectedActionKey, setSelectedActionKey] = useState<string | null>(
    null,
  );
  const [questionAnswers, setQuestionAnswers] = useState<
    Record<string, string>
  >({});
  const [otherText, setOtherText] = useState('');
  const [spendApprovalItems, setSpendApprovalItems] = useState<ReplyToItem[]>(
    [],
  );
  const [spendIsDeposit, setSpendIsDeposit] = useState(false);

  const actionDropdownRef = useRef<HTMLDivElement>(null);
  const mobileSheetRef = useRef<HTMLDivElement>(null);
  const { checkRadiusForField, radiusChecking, radiusResults } =
    useRadiusCheck();

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        actionDropdownRef.current &&
        !actionDropdownRef.current.contains(event.target as Node) &&
        !mobileSheetRef.current?.contains(event.target as Node)
      ) {
        setActionDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Desired actions from artifact
  const desiredActions = useMemo(() => {
    const actions: DesiredAction[] = artifact.desiredActions || [];
    return [...actions].sort((a, b) => {
      if (a.key === 'other') return 1;
      if (b.key === 'other') return -1;
      return 0;
    });
  }, [artifact.desiredActions]);

  const hasDesiredActions = desiredActions.length > 0;

  // Find the default action
  const defaultActionKey = useMemo(() => {
    const defaultAction = desiredActions.find((a) => a.default);
    return defaultAction?.key || desiredActions[0]?.key || null;
  }, [desiredActions]);

  // Auto-select default action and prefill default values
  useEffect(() => {
    setSelectedActionKey(defaultActionKey);
    setOtherText('');
    const defaultAction = desiredActions.find(
      (a) => a.key === defaultActionKey,
    );
    const prefills: Record<string, string> = {};
    for (const q of defaultAction?.clarifyingQuestions || []) {
      if (q.defaultValue) prefills[q.fieldKey] = q.defaultValue;
    }
    setQuestionAnswers(prefills);
  }, [desiredActions, defaultActionKey]);

  const selectedAction = useMemo(
    () => desiredActions.find((a) => a.key === selectedActionKey) || null,
    [desiredActions, selectedActionKey],
  );

  const questions: AnalyzeTodoClarifyingQuestion[] = useMemo(
    () =>
      selectedAction && selectedActionKey !== 'other'
        ? selectedAction.clarifyingQuestions || []
        : [],
    [selectedAction, selectedActionKey],
  );

  const handleActionChange = useCallback(
    (key: string) => {
      setSelectedActionKey(key);
      setQuestionAnswers({});
      setOtherText('');
      const newAction = desiredActions.find((a) => a.key === key);
      if (newAction?.clarifyingQuestions) {
        const prefills: Record<string, string> = {};
        for (const q of newAction.clarifyingQuestions) {
          if (q.defaultValue) prefills[q.fieldKey] = q.defaultValue;
        }
        if (Object.keys(prefills).length > 0) setQuestionAnswers(prefills);
      }
    },
    [desiredActions],
  );

  const isFormValid = useMemo(() => {
    return artifact.fields.every((field) => {
      if (!field.required) return true;
      if (field.fieldType === 'key_value') return true;
      const value = formValues[field.fieldId];
      return value !== null && value !== undefined && value !== '';
    });
  }, [artifact.fields, formValues]);

  // Check if confirm is disabled
  const isConfirmDisabled = useMemo(() => {
    if (submitting || !isFormValid) return true;
    if (!hasDesiredActions) return false;
    if (!selectedActionKey) return true;
    if (selectedActionKey === 'other' && !otherText.trim()) return true;
    const hasRequiredUnanswered = questions.some(
      (q) => q.required && !questionAnswers[q.fieldKey],
    );
    if (hasRequiredUnanswered) return true;
    const hasOutsideRadius = questions.some(
      (q) =>
        q.mustBeWithinRadius &&
        questionAnswers[q.fieldKey] &&
        radiusResults[q.fieldKey] === false,
    );
    if (hasOutsideRadius) return true;
    return false;
  }, [
    submitting,
    isFormValid,
    hasDesiredActions,
    selectedActionKey,
    otherText,
    questions,
    questionAnswers,
    radiusResults,
  ]);

  const handleSubmit = () => {
    if (!hasDesiredActions) {
      if (pendingSpendItems.length > 0) {
        setShowSpendApproval(true);
        return;
      }
      onSubmit();
      return;
    }

    // Has desired actions — build the action response
    if (!selectedAction) return;

    const isSpendingAction = SPENDING_ACTION_KEYS.has(
      selectedActionKey as DesiredActionKey,
    );
    const questionSpend = getQuestionSpendApproval(
      selectedAction,
      questionAnswers,
    );

    if (isSpendingAction && pendingSpendItems.length > 0) {
      setSpendApprovalItems(
        pendingSpendItems.map((item) => ({
          ...item,
          bookable: true,
        })),
      );
      setSpendIsDeposit(pendingSpendItems.some((i) => i.isDeposit));
      setShowSpendApproval(true);
      return;
    }

    if (questionSpend) {
      const symbol =
        getSymbolFromCurrency(questionSpend.currency) || questionSpend.currency;
      setSpendApprovalItems([
        {
          id: `qs-${artifact.id}`,
          actionLine: questionSpend.description,
          descriptionLine: `${questionSpend.isDeposit ? 'Deposit' : 'Spend'} up to ${symbol}${questionSpend.amount}`,
          price: questionSpend.amount,
          price_currency: questionSpend.currency,
          isDeposit: questionSpend.isDeposit,
          bookable: true,
        },
      ]);
      setSpendIsDeposit(questionSpend.isDeposit);
      setShowSpendApproval(true);
      return;
    }

    submitWithAction();
  };

  const submitWithAction = useCallback(
    (mandateItems?: any[], cardSel?: CardSelection) => {
      if (!selectedAction) return;
      const actionResponse: DesiredActionResponse = {
        key: selectedAction.key,
        label: getActionLabel(selectedAction),
        ...(selectedActionKey === 'other'
          ? { customText: otherText }
          : { questionAnswers }),
      };
      onSubmitWithAction(actionResponse, mandateItems, cardSel);
    },
    [
      selectedAction,
      selectedActionKey,
      otherText,
      questionAnswers,
      onSubmitWithAction,
    ],
  );

  const handleSpendApprove = (cardSel?: CardSelection) => {
    setShowSpendApproval(false);
    if (hasDesiredActions && selectedAction) {
      submitWithAction(spendApprovalItems, cardSel);
    } else {
      onApproveSpending(cardSel);
    }
  };

  const handleSpendCancel = () => {
    setShowSpendApproval(false);
  };

  const submitLabel = artifact.submitLabel || 'Submit';

  const renderField = (field: FormField) => {
    const value = formValues[field.fieldId] ?? '';

    switch (field.fieldType) {
      case 'text':
        return (
          <input
            type='text'
            className='fi-fs-input'
            placeholder={field.placeholder}
            value={value as string}
            onChange={(e) => onFieldChange(field.fieldId, e.target.value)}
            required={field.required}
            disabled={readOnly}
          />
        );
      case 'number':
        return (
          <input
            type='number'
            className='fi-fs-input'
            placeholder={field.placeholder}
            value={value as number}
            onChange={(e) =>
              onFieldChange(
                field.fieldId,
                e.target.value ? Number(e.target.value) : null,
              )
            }
            min={field.min}
            max={field.max}
            required={field.required}
            disabled={readOnly}
          />
        );
      case 'date':
        return (
          <input
            type='date'
            className='fi-fs-input'
            value={value as string}
            onChange={(e) => onFieldChange(field.fieldId, e.target.value)}
            required={field.required}
            disabled={readOnly}
          />
        );
      case 'textarea':
        return (
          <textarea
            className='fi-fs-textarea'
            placeholder={field.placeholder}
            value={value as string}
            onChange={(e) => onFieldChange(field.fieldId, e.target.value)}
            rows={field.rows || 4}
            required={field.required}
            disabled={readOnly}
          />
        );
      case 'select':
        return (
          <select
            className='fi-fs-select'
            value={value as string}
            onChange={(e) => onFieldChange(field.fieldId, e.target.value)}
            required={field.required}
            disabled={readOnly}
          >
            <option value=''>{field.placeholder || 'Select an option'}</option>
            {field.options?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );
      case 'key_value':
        return <div className='fi-fs-key-value'>{field.value || ''}</div>;
      default:
        return null;
    }
  };

  return (
    <div
      className={`fi-full-screen${sidebarExpanded ? ' fi-fs-expanded' : ''}`}
    >
      {sidebarExpanded && (
        <div
          className='fi-fs-backdrop'
          onClick={() => setSidebarExpanded(false)}
        />
      )}
      <div className='fi-fs-scroll-area'>
        {/* Back button */}
        <div className='fi-fs-top-bar'>
          <button className='fi-fs-back-btn' onClick={onClose}>
            <ChevronLeftIcon />
            <span>Back to Todo list</span>
          </button>
        </div>

        {/* Hero header */}
        <div className='fi-fs-hero'>
          <h1 className='fi-fs-title'>{artifact.title || 'Action required'}</h1>
          {!readOnly && (
            <button
              className='fi-fs-remove-btn fi-fs-mobile-only'
              onClick={onAcknowledge}
              disabled={submitting}
            >
              <NoLongerNeededIcon /> Remove task
            </button>
          )}
        </div>

        {/* Content */}
        <div className='fi-fs-content'>
          {artifact.overview && (
            <div className='fi-fs-overview'>
              <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
                {artifact.overview}
              </ReactMarkdown>
            </div>
          )}

          {/* Two-column layout */}
          <div className='fi-fs-columns'>
            {/* Left column: form fields */}
            <div className='fi-fs-form'>
              {artifact.fields.map((field) => (
                <div key={field.fieldId} className='fi-fs-field'>
                  <label className='fi-fs-label'>
                    {field.label}
                    {field.required && (
                      <span className='fi-fs-required'>*</span>
                    )}
                  </label>
                  {renderField(field)}
                </div>
              ))}
            </div>

            {/* Right column: sidebar */}
            {!readOnly && (
              <div
                className={`fi-fs-sidebar${sidebarExpanded ? ' expanded' : ''}`}
              >
                <div className='fi-fs-sidebar-inner'>
                  {hasDesiredActions && (
                    <div className='fi-fs-next-steps-area'>
                      {/* ── Mobile: compact nextstep bar ── */}
                      <div className='fi-fs-nextstep-bar'>
                        <div className='fi-fs-nextstep-bar-row'>
                          <div
                            className='fi-fs-dropdown-wrapper'
                            ref={actionDropdownRef}
                          >
                            <button
                              className={`fi-fs-action-btn-trigger${actionDropdownOpen ? ' active' : ''}`}
                              onClick={() => {
                                setActionDropdownOpen(!actionDropdownOpen);
                                if (!sidebarExpanded) setSidebarExpanded(true);
                              }}
                              disabled={submitting}
                            >
                              {selectedAction &&
                                ACTION_ICONS[
                                  selectedAction.key as DesiredActionKey
                                ] &&
                                React.createElement(
                                  ACTION_ICONS[
                                    selectedAction.key as DesiredActionKey
                                  ],
                                )}
                              {selectedAction
                                ? getActionKeyLabel(
                                    selectedAction.key as DesiredActionKey,
                                    agentName,
                                  )
                                : 'Select action'}
                              <ChevronDownIcon className='chevron-icon' />
                            </button>
                            {actionDropdownOpen && !isMobile && (
                              <div className='fi-fs-dropdown-menu'>
                                {desiredActions.map((action) => (
                                  <button
                                    key={action.key}
                                    className={`fi-fs-dropdown-item${action.key === selectedActionKey ? ' selected' : ''}`}
                                    onClick={() => {
                                      handleActionChange(action.key);
                                      setActionDropdownOpen(false);
                                    }}
                                  >
                                    {ACTION_ICONS[
                                      action.key as DesiredActionKey
                                    ] &&
                                      React.createElement(
                                        ACTION_ICONS[
                                          action.key as DesiredActionKey
                                        ],
                                      )}
                                    {getActionKeyLabel(
                                      action.key as DesiredActionKey,
                                      agentName,
                                    )}
                                  </button>
                                ))}
                              </div>
                            )}
                            {actionDropdownOpen &&
                              isMobile &&
                              createPortal(
                                <div
                                  className='mc-fs-dropdown-sheet-backdrop'
                                  onClick={() => setActionDropdownOpen(false)}
                                >
                                  <div
                                    className='mc-fs-dropdown-sheet'
                                    ref={mobileSheetRef}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <div className='mc-fs-dropdown-sheet-header'>
                                      What would you like to do?
                                    </div>
                                    {desiredActions.map((action) => (
                                      <button
                                        key={action.key}
                                        className={`mc-fs-dropdown-item${action.key === selectedActionKey ? ' selected' : ''}`}
                                        onClick={() => {
                                          handleActionChange(action.key);
                                          setActionDropdownOpen(false);
                                        }}
                                      >
                                        {ACTION_ICONS[
                                          action.key as DesiredActionKey
                                        ] &&
                                          React.createElement(
                                            ACTION_ICONS[
                                              action.key as DesiredActionKey
                                            ],
                                          )}
                                        <div className='mc-fs-dropdown-item-text'>
                                          <span className='mc-fs-dropdown-item-title'>
                                            {getActionKeyLabel(
                                              action.key as DesiredActionKey,
                                              agentName,
                                            )}
                                          </span>
                                          <span className='mc-fs-dropdown-item-subtitle'>
                                            {getActionLabel(action)}
                                          </span>
                                        </div>
                                      </button>
                                    ))}
                                  </div>
                                </div>,
                                document.body,
                              )}
                          </div>
                          {!sidebarExpanded ? (
                            <button
                              className='fi-fs-next-btn'
                              onClick={() => setSidebarExpanded(true)}
                            >
                              Next
                            </button>
                          ) : (
                            <button
                              className='fi-fs-close-sidebar-btn'
                              onClick={() => setSidebarExpanded(false)}
                            >
                              <CloseXIcon />
                            </button>
                          )}
                        </div>
                      </div>

                      {/* ── Desktop: Next steps header + dropdown ── */}
                      <div className='fi-fs-section fi-fs-nextsteps fi-fs-desktop-only'>
                        <div className='fi-fs-section-title'>Next steps</div>
                        <div
                          className='fi-fs-dropdown-wrapper'
                          ref={actionDropdownRef}
                        >
                          <button
                            className={`fi-fs-action-btn-trigger${actionDropdownOpen ? ' active' : ''}`}
                            onClick={() =>
                              setActionDropdownOpen(!actionDropdownOpen)
                            }
                            disabled={submitting}
                          >
                            {selectedAction &&
                              ACTION_ICONS[
                                selectedAction.key as DesiredActionKey
                              ] &&
                              React.createElement(
                                ACTION_ICONS[
                                  selectedAction.key as DesiredActionKey
                                ],
                              )}
                            {selectedAction
                              ? getActionKeyLabel(
                                  selectedAction.key as DesiredActionKey,
                                  agentName,
                                )
                              : 'Select action'}
                            <ChevronDownIcon className='chevron-icon' />
                          </button>
                          {actionDropdownOpen && (
                            <div className='fi-fs-dropdown-menu'>
                              {desiredActions.map((action) => (
                                <button
                                  key={action.key}
                                  className={`fi-fs-dropdown-item${action.key === selectedActionKey ? ' selected' : ''}`}
                                  onClick={() => {
                                    handleActionChange(action.key);
                                    setActionDropdownOpen(false);
                                  }}
                                >
                                  {ACTION_ICONS[
                                    action.key as DesiredActionKey
                                  ] &&
                                    React.createElement(
                                      ACTION_ICONS[
                                        action.key as DesiredActionKey
                                      ],
                                    )}
                                  {getActionKeyLabel(
                                    action.key as DesiredActionKey,
                                    agentName,
                                  )}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* ── Detail sections (desktop: always visible, mobile: only when expanded) ── */}
                      <div className='fi-fs-next-steps-detail'>
                        {/* Clarifying questions */}
                        {selectedActionKey !== 'other' &&
                          questions.length > 0 && (
                            <div className='fi-fs-questions'>
                              {questions.map((q) => {
                                const isCheckingRadius =
                                  q.mustBeWithinRadius &&
                                  radiusChecking[q.fieldKey];
                                const isOutsideRadius =
                                  q.mustBeWithinRadius &&
                                  questionAnswers[q.fieldKey] &&
                                  radiusResults[q.fieldKey] === false;

                                return (
                                  <div
                                    key={q.fieldKey}
                                    className={`fi-fs-question${isOutsideRadius ? ' outside-radius' : ''}`}
                                  >
                                    <label>
                                      {q.label}
                                      {q.required && (
                                        <span className='fi-fs-required'>
                                          *
                                        </span>
                                      )}
                                    </label>
                                    {q.type === 'select' ? (
                                      <select
                                        className='fi-fs-select'
                                        value={
                                          questionAnswers[q.fieldKey] || ''
                                        }
                                        onChange={(e) =>
                                          setQuestionAnswers((prev) => ({
                                            ...prev,
                                            [q.fieldKey]: e.target.value,
                                          }))
                                        }
                                      >
                                        <option value=''>Select...</option>
                                        {(q.options || []).map((opt) => (
                                          <option key={opt} value={opt}>
                                            {opt}
                                          </option>
                                        ))}
                                      </select>
                                    ) : (
                                      <div className='fi-fs-input-wrapper'>
                                        {q.type === 'number' && q.currency && (
                                          <span className='fi-fs-currency-prefix'>
                                            {getSymbolFromCurrency(
                                              q.currency,
                                            ) || q.currency}
                                          </span>
                                        )}
                                        {q.type === 'textarea' ? (
                                          <textarea
                                            className='fi-fs-textarea'
                                            value={
                                              questionAnswers[q.fieldKey] || ''
                                            }
                                            onChange={(e) =>
                                              setQuestionAnswers((prev) => ({
                                                ...prev,
                                                [q.fieldKey]: e.target.value,
                                              }))
                                            }
                                            placeholder=''
                                            rows={3}
                                          />
                                        ) : (
                                          <input
                                            type={
                                              q.type === 'number'
                                                ? 'number'
                                                : 'text'
                                            }
                                            className='fi-fs-input'
                                            value={
                                              questionAnswers[q.fieldKey] || ''
                                            }
                                            onChange={(e) =>
                                              setQuestionAnswers((prev) => ({
                                                ...prev,
                                                [q.fieldKey]: e.target.value,
                                              }))
                                            }
                                            onBlur={() => {
                                              if (
                                                q.mustBeWithinRadius &&
                                                questionAnswers[q.fieldKey]
                                              ) {
                                                checkRadiusForField(
                                                  q.fieldKey,
                                                  questionAnswers[q.fieldKey],
                                                );
                                              }
                                            }}
                                            placeholder=''
                                          />
                                        )}
                                      </div>
                                    )}
                                    {isCheckingRadius && (
                                      <span className='fi-fs-field-hint checking'>
                                        Checking area...
                                      </span>
                                    )}
                                    {isOutsideRadius && (
                                      <span className='fi-fs-field-hint error'>
                                        Outside Dreamster operating area
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}

                        {/* Other text input */}
                        {selectedActionKey === 'other' && (
                          <div className='fi-fs-questions'>
                            <div className='fi-fs-question'>
                              <label>
                                What would you like done?
                                <span className='fi-fs-required'>*</span>
                              </label>
                              <textarea
                                className='fi-fs-textarea'
                                value={otherText}
                                onChange={(e) => setOtherText(e.target.value)}
                                placeholder=''
                                rows={3}
                              />
                            </div>
                          </div>
                        )}

                        {/* Spending approval modal */}
                        <SpendingApprovalModal
                          show={showSpendApproval}
                          onApprove={handleSpendApprove}
                          onCancel={handleSpendCancel}
                          items={spendApprovalItems}
                          agentImageUrl={agentImageUrl}
                          isDeposit={spendIsDeposit}
                        />

                        {/* Confirm button */}
                        {!showSpendApproval && (
                          <div className='fi-fs-confirm-wrapper'>
                            {selectedAction && (
                              <p className='fi-fs-action-label'>
                                {getActionLabel(selectedAction)}
                              </p>
                            )}
                            <button
                              className='fi-fs-action-btn'
                              onClick={handleSubmit}
                              disabled={isConfirmDisabled}
                            >
                              {submitLabel}
                            </button>
                          </div>
                        )}
                      </div>
                      {/* end fi-fs-next-steps-detail */}
                    </div>
                  )}

                  {/* Simple confirm when no desired actions */}
                  {!hasDesiredActions && (
                    <div className='fi-fs-action-area'>
                      <SpendingApprovalModal
                        show={showSpendApproval}
                        onApprove={handleSpendApprove}
                        onCancel={handleSpendCancel}
                        items={pendingSpendItems}
                        agentImageUrl={agentImageUrl}
                        isDeposit={pendingSpendItems.some((i) => i.isDeposit)}
                      />
                      {!showSpendApproval && (
                        <div className='fi-fs-confirm-wrapper'>
                          <button
                            className='fi-fs-action-btn'
                            onClick={handleSubmit}
                            disabled={submitting || !isFormValid}
                          >
                            {submitLabel}
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {onChatAbout && (
                    <button className='fi-fs-chat-btn' onClick={onChatAbout}>
                      <SpeechBubbleIcon />
                      {chatButtonText}
                    </button>
                  )}

                  {!readOnly && (
                    <button
                      className='fi-fs-remove-btn fi-fs-desktop-only'
                      onClick={onAcknowledge}
                      disabled={submitting}
                    >
                      <NoLongerNeededIcon /> Remove task
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormInputFullScreen;
